import { useCallback, useMemo } from 'react';
import useGlobals from './useGlobals';
import { setIsSending } from '../Store/Actions/chatActions';
import toast from 'react-hot-toast';
import Resizer from 'react-image-file-resizer';
import { chatService } from '../Services/chatService';
import { setFileDlpModalState } from '../Store/Actions/modalActions';
import { DLPState } from '../Types/Enums';

const useDocuments = ({
    setFiles,
    setTmpFileNames,
    textInputRef,
    setImages,
    setTmpImageUrls,
    model,
    i18n,
}: {
    setFiles: any;
    setTmpFileNames: any;
    textInputRef: any;
    setImages: any;
    setTmpImageUrls: any;
    model: any;
    i18n: any;
}) => {
    const { dispatch } = useGlobals();

    const resizeFile = useCallback(
        (file: File, setTmpImageUrls?: any, setImages?: any) => {
            new Promise(() => {
                const maxFileSize = 64 * 1024 * 1024;
                if (file.size <= maxFileSize && file.type.startsWith('image/')) {
                    Resizer.imageFileResizer(
                        file,
                        2048,
                        2048,
                        'JPEG',
                        80,
                        0,
                        (blob) => {
                            if (blob instanceof Blob) {
                                const imageFile = new File([blob], file.name);
                                if (setTmpImageUrls)
                                    setTmpImageUrls((prevImageUrls: any) => [...prevImageUrls, file.name]);

                                const formData = new FormData();
                                formData.append('image', imageFile);

                                chatService.uploadImage(formData).then((response: any) => {
                                    if (setImages) setImages((prevImages: any) => [...prevImages, response.data]);
                                    dispatch(setIsSending(false));
                                });
                            }
                        },
                        'blob',
                    );
                } else {
                    const formData = new FormData();
                    formData.append('image', file);

                    chatService.uploadImage(formData).then((response: any) => {
                        if (setImages) setImages((prevImages: any) => [...prevImages, response.data]);
                        if (setTmpImageUrls) {
                            setTmpImageUrls((prevImageUrls: any) => [...prevImageUrls, file.name]);
                        }
                        dispatch(setIsSending(false));
                    });
                }
            });
        },
        [dispatch],
    );

    const resizeFileHandler = useCallback(
        (file: File) => {
            resizeFile(file, setTmpImageUrls, setImages);
        },
        [resizeFile, setTmpImageUrls, setImages],
    );

    const handleFileChange = useCallback(
        async (event: React.ChangeEvent<HTMLInputElement>, promptIdentifier: string, combinedDlp: DLPState | null) => {
            textInputRef.current.focus();
            dispatch(setIsSending(true));

            const files = event.target.files;
            if (!files || files.length === 0) {
                dispatch(setIsSending(false));
                return;
            }

            const maxFileSize = 64 * 1024 * 1024;
            const lowerCaseAllowedMimeTypes = model?.allowedMimeTypes?.map((mimeType: string) =>
                mimeType.toLowerCase(),
            );

            let allFiles: any[] = [];
            const uploadPromises = Array.from(files).map(async (file) => {
                if (!lowerCaseAllowedMimeTypes?.includes(file.type.toLowerCase())) {
                    toast.error(
                        i18n.translate({
                            id: 'fileUpload.error.fileType',
                            sourceName: 'FileUpload',
                            params: {
                                fileName: file.name,
                            },
                        }),
                    );
                    return;
                }

                if (file.size > maxFileSize) {
                    toast.error(
                        i18n.translate({
                            id: 'fileUpload.error.fileSize',
                            sourceName: 'FileUpload',
                            params: {
                                fileName: file.name,
                            },
                        }),
                    );
                    return;
                }

                try {
                    const formData = new FormData();
                    formData.append('file', file);
                    formData.append('promptIdentifier', promptIdentifier);

                    const response = await chatService.uploadFile(formData);

                    if (response.data.error) {
                        toast.error(response.data.error);
                        return;
                    }

                    allFiles.push(response.data);
                } catch (error) {
                    toast.error(
                        i18n.translate({
                            id: 'fileUpload.error.upload',
                            sourceName: 'FileUpload',
                            params: {
                                fileName: file.name,
                            },
                        }),
                    );
                    console.error('File upload error:', error);
                }
            });

            try {
                await Promise.all(uploadPromises);

                if (allFiles.length > 0) {
                    // Skip DLP check if DLP is disabled or explicitly set to off
                    if (combinedDlp !== DLPState.dlp_off) {
                        dispatch(
                            setFileDlpModalState({
                                state: 'open',
                                files: allFiles,
                                combinedDlp: combinedDlp ?? DLPState.dlp_on,
                                callback: (files: any[]) => {
                                    setFiles((prevFiles: any) => [...prevFiles, ...files]);
                                },
                            }),
                        );
                    } else {
                        // DLP is disabled, add all files directly without checking
                        setFiles((prevFiles: any) => [...prevFiles, ...allFiles]);
                    }
                }
            } catch (error) {
                console.error('Error during file uploads:', error);
            } finally {
                dispatch(setIsSending(false));
                textInputRef.current.focus();
            }
        },
        [dispatch, textInputRef, model?.allowedMimeTypes, i18n],
    );

    const handleFileDelete = useCallback(
        (index: number) => {
            setFiles((prevFiles: any) => {
                const updatedFiles = prevFiles.filter((_: any, i: number) => i !== index);
                return updatedFiles;
            });

            setTmpFileNames((prevTmpFileNames: any) => {
                const updatedFileNames = prevTmpFileNames.filter((_: any, i: number) => i !== index);
                return updatedFileNames;
            });

            textInputRef.current.focus();
        },
        [setFiles, setTmpFileNames, textInputRef],
    );

    const handleImageChange = useCallback(
        async (event: React.ChangeEvent<HTMLInputElement>) => {
            dispatch(setIsSending(true));
            const file = event.target.files ? event.target.files[0] : null;
            if (file) {
                resizeFileHandler(file);
            }
            textInputRef.current.focus();
        },
        [resizeFileHandler, dispatch, textInputRef],
    );

    const handleImageDelete = useCallback(
        (index: number) => {
            setImages((prevImages: any) => {
                const updatedImages = prevImages.filter((_: any, i: number) => i !== index);
                return updatedImages;
            });

            setTmpImageUrls((prevTmpImageUrls: any) => {
                const updatedImageUrls = prevTmpImageUrls.filter((_: any, i: number) => i !== index);
                return updatedImageUrls;
            });

            textInputRef.current.focus();
        },
        [setImages, setTmpImageUrls, textInputRef],
    );

    const DocumentsActions = useMemo(
        () => ({
            handleFileChange,
            handleFileDelete,
            handleImageChange,
            handleImageDelete,
            resizeFileHandler,
        }),
        [handleFileChange, handleFileDelete, handleImageChange, handleImageDelete, resizeFileHandler],
    );

    return { DocumentsActions, resizeFileHandler };
};

export default useDocuments;
